import { ChevronLeft, ChevronRight, Maximize } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem,
} from '@/components/ui/carousel'
import { cn } from '@/lib/utils'
import type { NoticeMedia as NoticeMediaData } from '@/types/notice'

interface NoticeMediaProps {
	media: NoticeMediaData[]
	/** Sobreposição (marca d'água) que precisa acompanhar a mídia, inclusive em tela cheia */
	overlay?: ReactNode
}

const controlClassName =
	'absolute top-1/2 z-20 size-9 -translate-y-1/2 border-0 bg-black/40 text-white hover:bg-black/60 hover:text-white'

/**
 * Renderiza a mídia do aviso: um vídeo, uma imagem ou, com várias imagens, um carrossel. A tela cheia
 * é aplicada ao container (e não ao <video>) para que a marca d'água continue visível; a tela cheia
 * nativa do vídeo é redirecionada para o container.
 */
export function NoticeMedia({ media, overlay }: NoticeMediaProps) {
	const { t } = useTranslation()
	const containerRef = useRef<HTMLDivElement>(null)
	const videoRef = useRef<HTMLVideoElement>(null)
	const [carouselApi, setCarouselApi] = useState<CarouselApi>()
	const [current, setCurrent] = useState(0)

	useEffect(() => {
		const handleFullscreenChange = () => {
			if (
				document.fullscreenElement &&
				document.fullscreenElement === videoRef.current
			) {
				void document
					.exitFullscreen()
					.then(() => containerRef.current?.requestFullscreen())
			}
		}
		document.addEventListener('fullscreenchange', handleFullscreenChange)
		return () =>
			document.removeEventListener('fullscreenchange', handleFullscreenChange)
	}, [])

	useEffect(() => {
		if (!carouselApi) return
		const sync = () => setCurrent(carouselApi.selectedScrollSnap())
		sync()
		carouselApi.on('select', sync)
		carouselApi.on('reInit', sync)
		return () => {
			carouselApi.off('select', sync)
			carouselApi.off('reInit', sync)
		}
	}, [carouselApi])

	const scrollTo = useCallback(
		(index: number) => carouselApi?.scrollTo(index),
		[carouselApi],
	)

	const toggleFullscreen = () => {
		if (document.fullscreenElement) {
			void document.exitFullscreen()
			return
		}
		void containerRef.current?.requestFullscreen()
	}

	const first = media[0]
	if (!first) return null

	const isVideo = first.kind === 'video'
	const isCarousel = !isVideo && media.length > 1

	return (
		<div
			ref={containerRef}
			className="relative flex items-center justify-center overflow-hidden rounded-lg bg-black select-none"
			onContextMenu={(event) => event.preventDefault()}
		>
			{isVideo ? (
				// biome-ignore lint/a11y/useMediaCaption: vídeos de avisos não possuem legenda
				<video
					ref={videoRef}
					src={first.url}
					controls
					preload="metadata"
					playsInline
					controlsList="nodownload nofullscreen noremoteplayback"
					disablePictureInPicture
					className="max-h-[70vh] w-full"
				/>
			) : isCarousel ? (
				<Carousel
					setApi={setCarouselApi}
					opts={{ loop: media.length > 1 }}
					className="w-full"
				>
					<CarouselContent className="ml-0">
						{media.map((item, index) => (
							<CarouselItem
								key={item.id}
								className="flex items-center justify-center pl-0"
								aria-label={t('notices.detail.imageCounter', {
									current: index + 1,
									total: media.length,
								})}
							>
								<img
									src={item.url}
									alt=""
									draggable={false}
									className="max-h-[70vh] w-full object-contain"
								/>
							</CarouselItem>
						))}
					</CarouselContent>
					<Button
						type="button"
						size="icon"
						variant="ghost"
						onClick={() => carouselApi?.scrollPrev()}
						aria-label={t('notices.detail.previousImage')}
						className={cn(controlClassName, 'left-2')}
					>
						<ChevronLeft className="size-5" />
					</Button>
					<Button
						type="button"
						size="icon"
						variant="ghost"
						onClick={() => carouselApi?.scrollNext()}
						aria-label={t('notices.detail.nextImage')}
						className={cn(controlClassName, 'right-2')}
					>
						<ChevronRight className="size-5" />
					</Button>
				</Carousel>
			) : (
				<img
					src={first.url}
					alt=""
					draggable={false}
					className="max-h-[70vh] w-full object-contain"
				/>
			)}
			{overlay}
			{isCarousel && (
				<div className="absolute bottom-2 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/50 px-3 py-1.5">
					<span className="font-medium text-[11px] text-white tabular-nums">
						{t('notices.detail.imageCounter', {
							current: current + 1,
							total: media.length,
						})}
					</span>
					<div className="flex items-center gap-1">
						{media.map((item, index) => (
							<button
								key={item.id}
								type="button"
								onClick={() => scrollTo(index)}
								aria-label={t('notices.detail.goToImage', { index: index + 1 })}
								aria-current={index === current}
								className={cn(
									'size-1.5 rounded-full transition-colors',
									index === current ? 'bg-white' : 'bg-white/40',
								)}
							/>
						))}
					</div>
				</div>
			)}
			<Button
				type="button"
				size="icon"
				variant="ghost"
				onClick={toggleFullscreen}
				aria-label={t('notices.detail.fullscreen')}
				className="absolute top-2 right-2 z-20 size-8 bg-black/40 text-white hover:bg-black/60 hover:text-white"
			>
				<Maximize className="size-4" />
			</Button>
		</div>
	)
}
